/**
 * Réponse du microservice utilisateurs à une réinitialisation de mots de passe. Il répond
 * 200 même quand des utilisateurs échouent : c'est `isCompleted` et `error.notification`
 * qui le disent, un échec par ligne de `notification`.
 */
export interface ResetUsersResponse {
    isCompleted?: boolean;
    userNotification?: string;
    error?: { notification?: string } | null;
    payload?: { data?: unknown } | null;
}

export interface ResetUsersSummary {
    ok: boolean;
    message: string;
}

/**
 * Causes connues, renvoyées en anglais par le microservice avec l'identifiant de
 * l'utilisateur, et leur libellé pour l'écran. Une cause absente d'ici s'affiche telle quelle.
 */
const KNOWN_CAUSES: { pattern: RegExp; label: string }[] = [
    {
        pattern: /^user (\S+) is SSO\b/,
        label: "compte SSO, son mot de passe est géré par le fournisseur d'identité du client",
    },
    {
        pattern: /^user (\S+) not found\b/,
        label: 'utilisateur introuvable',
    },
    {
        pattern: /^password of (\S+) was reset but the email could not be sent\b/,
        label: "l'email n'a pas pu être envoyé, relancez la réinitialisation",
    },
];

/**
 * Mode faux email : les mots de passe sont déjà changés quand le mail au support échoue,
 * et ce mail était le seul à les porter. La ligne ne concerne pas un utilisateur mais l'envoi.
 */
const SUPPORT_MAIL_FAILED = /^passwords of (\d+) users were reset but the email to the support could not be sent\b/;

function describeFailure(line: string, mailById: { [id: string]: string }): string {
    const supportMail = SUPPORT_MAIL_FAILED.exec(line);
    if (supportMail != null) {
        return `${supportMail[1]} mot(s) de passe réinitialisé(s), mais le mail au support n'a pas pu être envoyé : `
            + 'personne ne les connaît, relancez la réinitialisation (faux email)';
    }
    for (const known of KNOWN_CAUSES) {
        const match = known.pattern.exec(line);
        if (match != null) {
            return `${mailById[match[1]] ?? match[1]} : ${known.label}`;
        }
    }
    return line;
}

/**
 * Ce que l'écran annonce après une réinitialisation. Tout est passé : à qui le mot de passe
 * a été envoyé. Au moins un échec : lequel et pourquoi, ligne par ligne. La modale annonçait
 * sinon un email même pour un compte SSO que le microservice avait refusé.
 */
export function summarizeResetResponse(
    response: ResetUsersResponse | null | undefined,
    fakeEmail: boolean,
    mailById: { [id: string]: string },
): ResetUsersSummary {
    const failures = (response?.error?.notification ?? '')
        .split('\n')
        .map(line => line.trim())
        .filter(line => line !== '');
    if (response?.isCompleted !== false && failures.length === 0) {
        const data = response?.payload?.data;
        const count = Array.isArray(data) ? data.length : 0;
        return {
            ok: true,
            message: fakeEmail
                ? `Mots de passe provisoires envoyés au support pour ${count} utilisateur(s).`
                : `Un email avec un mot de passe provisoire a été envoyé à ${count} utilisateur(s).`,
        };
    }
    const detail = failures.length > 0
        ? '\n\n' + failures.map(line => describeFailure(line, mailById)).join('\n')
        : '';
    return {
        ok: false,
        message: `Réinitialisation terminée avec des erreurs.${detail}`,
    };
}
